import { useQuery } from '@apollo/client';

import { SummaryReturn } from '@eluve/frontend-appointment-hooks';
import { DbEnums } from '@eluve/graphql-types';
import {
  DynamicArtifactTemplate,
  DynamicSummary,
  LegacySOAPOutputTemplate,
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';

import { getGoldenNoteSummary } from './operations';

export type GoldenNote = {
  id: string;
  humanSummary: DynamicSummary | null;
  outputTemplate: DynamicArtifactTemplate | undefined;
  status: string;
  createdAt: string;
  updatedAt: string;
};

type GoldenSummaryReturn = {
  goldenNote: GoldenNote | null;
  originalSummary: SummaryReturn;
  originalContent: Record<string, unknown> | null;
  refetch: () => void;
};

/**
 * Returns a single golden note for an appointment
 */
export const useGoldenSummaries = (
  tenantId: string,
  appointmentId: string,
  activeNoteId: string,
): GoldenSummaryReturn => {
  const { data, refetch } = useQuery(getGoldenNoteSummary, {
    variables: {
      tenantId,
      appointmentId,
      activeNoteId,
    },
    fetchPolicy: 'network-only',
  });

  const appointmentMode = data?.appointmentsByPk?.mode;
  const note = data?.appointmentsByPk?.appointment_golden_notes?.[0] ?? null;

  const humanOutput = data?.appointmentsByPk?.humanOutputs?.[0];
  const humanOutputContent = humanOutput?.output?.content;

  const llmOutput = humanOutput?.output?.llm_output;
  const llmOutputContent = llmOutput?.content;
  const llmOutputMetadata = llmOutput?.metadata;
  const llmOutputType = llmOutput?.modelPromptTemplate?.template?.outputType;

  let outputTemplate: DynamicArtifactTemplate | undefined = undefined;

  // If the output type is SOAP_NOTE, we need to use the legacy template
  if (llmOutputType === DbEnums.LlmOutputType.SOAP_NOTE) {
    outputTemplate = LegacySOAPOutputTemplate;
  } else if (appointmentMode === DbEnums.AppointmentMode.MANUAL_CHARTING) {
    // If we started the appointment in manual charting mode, we need to use the
    // output template from `appointment_human_outputs`
    const outputTemplateRaw = humanOutput?.output_template?.template;
    if (outputTemplateRaw) {
      outputTemplate = dynamicArtifactTemplateSchema.parse(outputTemplateRaw);
    }
  } else if (appointmentMode === DbEnums.AppointmentMode.AUDIO_COPILOT) {
    // If this is an audio co-pilot appointment, we need to use the output template
    // from the LLM output metadata
    const outputTemplateRaw = llmOutputMetadata?.output_variant?.template;
    if (outputTemplateRaw) {
      outputTemplate = dynamicArtifactTemplateSchema.parse(outputTemplateRaw);
    }
  }

  if (!outputTemplate || (!llmOutputContent && !humanOutputContent)) {
    const returnSummary = {
      isSummaryAvailable: false,
      isEmpty: true,
      llmOutputId: null,
      humanOutputId: null,
      refetch,
      summary: null,
    };
    return {
      originalSummary: returnSummary,
      goldenNote: null,
      refetch,
      originalContent: humanOutputContent ?? llmOutputContent ?? {},
    };
  }

  const hydrated = hydrateDynamicArtifactTemplate(
    outputTemplate,
    humanOutputContent ?? llmOutputContent ?? {},
  );

  const hydratedGoldenNote =
    note && note.content
      ? ({
          id: note.id,
          humanSummary: {
            type: 'DYNAMIC' as const,
            blocks: hydrateDynamicArtifactTemplate(outputTemplate, note.content)
              .blocks,
          },
          outputTemplate: outputTemplate,
          status: note.status,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
        } as GoldenNote)
      : null;

  const returnSummary = {
    humanOutputId: null,
    llmOutputId: null,
    isSummaryAvailable: true,
    isEmpty: false,
    refetch,
    summary: {
      type: 'DYNAMIC' as const,
      blocks: hydrated.blocks,
    },
  };
  return {
    originalSummary: returnSummary,
    goldenNote: hydratedGoldenNote,
    refetch,
    originalContent: humanOutputContent ?? llmOutputContent ?? {},
  };
};

// Store for tracking pending changes
export const pendingGoldenSummaryChangesStore = {
  state: false,
  setState: (value: boolean) => {
    pendingGoldenSummaryChangesStore.state = value;
  },
};
